/**
 * FileList
 *
 * Renders files as a grid of FileCards or a table. Toggleable via `view` prop.
 */

import React from 'react';
import { Download, Trash2, Lock, Shield, Key } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileRecord } from '@/services/api/fileService';
import { FileCard } from './FileCard';

interface FileListProps {
  files: FileRecord[];
  view: 'grid' | 'list';
  onSelect?: (file: FileRecord) => void;
  onDownload?: (file: FileRecord) => void;
  onDelete?: (file: FileRecord) => void;
  showFileSize?: boolean;
  showUploadDate?: boolean;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

export const FileList: React.FC<FileListProps> = ({ files, view, onSelect, onDownload, onDelete, showFileSize = true, showUploadDate = true }) => {
  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No files found
      </div>
    );
  }

  if (view === 'grid') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {files.map((file) => (
          <FileCard key={file._id} file={file} onSelect={onSelect} onDownload={onDownload} onDelete={onDelete} showFileSize={showFileSize} />
        ))}
      </div>
    );
  }

  // List/table view
  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-muted text-left text-sm">
            <th className="p-2 font-medium">Name</th>
            {showFileSize !== false && <th className="p-2 font-medium">Size</th>}
            <th className="p-2 font-medium">Type</th>
            <th className="p-2 font-medium">Visibility</th>
            <th className="p-2 font-medium">Encryption</th>
            {showUploadDate !== false && <th className="p-2 font-medium">Modified</th>}
            <th className="p-2 font-medium w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <tr
              key={file._id}
              className="border-t hover:bg-muted/30 cursor-pointer text-sm"
              onClick={() => onSelect?.(file)}
            >
              <td className="p-2 font-medium truncate max-w-[200px]">{file.filename}</td>
              {showFileSize !== false && <td className="p-2 text-muted-foreground">{formatSize(file.size)}</td>}
              <td className="p-2">
                <span className="text-xs text-muted-foreground">{file.contentType.split('/').pop()}</span>
              </td>
              <td className="p-2">
                <Badge variant="outline" className="text-xs">{file.visibility}</Badge>
              </td>
              <td className="p-2">
                {file.encryptionType !== 'none' && (
                  <Badge variant="secondary" className="text-xs">{file.encryptionType}</Badge>
                )}
              </td>
              {showUploadDate !== false && <td className="p-2 text-xs text-muted-foreground">{formatDate(file.updatedAt)}</td>}
              <td className="p-2">
                <div className="flex gap-1">
                  {onDownload && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); onDownload(file); }}>
                      <Download className="w-3 h-3" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
